import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Badge,
  Tooltip,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Message as MessageIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  LocalHospital as MedicalIcon,
  EventNote as GranolaIcon,
  Notifications as NotificationsIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  AutoAwesome as AskAiIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import { useBrandingStore } from '../../store/brandingStore';
import { useUiChromeStore } from '../../store/uiChromeStore';
import MobileBottomNav from './MobileBottomNav';
import { authApi, notificationApi, settingsApi } from '../../services/api';
import { setPracticeTimeZone } from '../../utils/practiceTime';
import { NotificationItem } from '../../types';

const drawerWidth = 260;
const collapsedDrawerWidth = 72;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Calendar | Scheduling', icon: <CalendarIcon />, path: '/calendar' },
  { text: 'Contacts', icon: <PeopleIcon />, path: '/contacts' },
  { text: 'Granola Notes', icon: <GranolaIcon />, path: '/granola' },
  { text: 'Bulk Messaging', icon: <MessageIcon />, path: '/bulk-messaging' },
  { text: 'Billing', icon: <ReceiptIcon />, path: '/billing' },
  { text: 'Ask Levitan AI', icon: <AskAiIcon />, path: '/ask-ai' },
  { text: 'Admin', icon: <SettingsIcon />, path: '/admin' },
];

const MainLayout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // immersive = a page (Contacts patient/chat) has taken over the bottom of the
  // screen, so the global bottom nav hides and its content padding is dropped.
  const immersive = useUiChromeStore((s) => s.immersive);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; }
  });
  const effectiveDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem('sidebarCollapsed', next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [fromMobile, setFromMobile] = useState<boolean>(() => {
    try { return sessionStorage.getItem('from_m') === '1'; } catch { return false; }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const { branding, fetchBranding } = useBrandingStore();

  // Fetch branding on mount
  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // Clinic clock. Default Chicago until Admin says otherwise.
  useEffect(() => {
    let cancelled = false;
    settingsApi.get('practice_timezone').then((res) => {
      const zone = res?.data?.setting?.value;
      if (!cancelled && typeof zone === 'string') setPracticeTimeZone(zone);
    }).catch(() => { /* keep America/Chicago */ });
    return () => { cancelled = true; };
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const [listResponse, countResponse] = await Promise.all([
        notificationApi.getNotifications(),
        notificationApi.getUnreadCount(),
      ]);
      const list = listResponse.data?.data || [];
      const count = countResponse.data?.data?.count ?? 0;
      setNotifications(Array.isArray(list) ? list : []);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  // Presence/online tracking (prod-only): ping the backend on mount and hourly
  // so the user's last-seen stays fresh. Preserved through the ux-ben merge.
  const sendHeartbeat = useCallback(async () => {
    try {
      await authApi.heartbeat();
    } catch (error) {
      console.error('Failed to send heartbeat:', error);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sendHeartbeat, user]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    setSelectedNotification(notification);
    setNotificationDialogOpen(true);
    handleNotificationsClose();
    if (!notification.read) {
      try {
        await notificationApi.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((item) => (item.id === notification.id ? { ...item, read: true } : item))
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  const handleBackToTriage = () => {
    let dest = '/m/';
    try {
      const saved = sessionStorage.getItem('from_m_return');
      if (saved && saved.startsWith('/m')) dest = saved;
      sessionStorage.removeItem('from_m');
      sessionStorage.removeItem('from_m_return');
    } catch { /* noop */ }
    setFromMobile(false);
    window.location.href = dest;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const renderDrawer = (collapsed: boolean) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          p: collapsed ? 1.5 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 1,
          minHeight: 64,
        }}
      >
        <Tooltip title={collapsed && !isMobile ? 'Expand sidebar' : ''} placement="right" disableHoverListener={!collapsed || isMobile}>
          <Box
            onClick={collapsed && !isMobile ? toggleSidebarCollapsed : undefined}
            role={collapsed && !isMobile ? 'button' : undefined}
            aria-label={collapsed && !isMobile ? 'Expand sidebar' : undefined}
            tabIndex={collapsed && !isMobile ? 0 : -1}
            onKeyDown={(e) => {
              if (collapsed && !isMobile && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                toggleSidebarCollapsed();
              }
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              borderRadius: 1,
              p: collapsed && !isMobile ? 0.5 : 0,
              cursor: collapsed && !isMobile ? 'pointer' : 'default',
              transition: 'background-color 0.15s',
              '&:hover': collapsed && !isMobile ? { backgroundColor: 'action.hover' } : undefined,
              '&:focus-visible': collapsed && !isMobile ? { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 } : undefined,
            }}
          >
            {branding.logoUrl ? (
              <Box
                component="img"
                src={branding.logoUrl}
                alt="Logo"
                sx={{ width: 32, height: 32, objectFit: 'contain', flexShrink: 0 }}
              />
            ) : (
              <MedicalIcon sx={{ fontSize: 32, color: 'primary.main', flexShrink: 0 }} />
            )}
            {!collapsed && (
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {branding.headerText}
              </Typography>
            )}
          </Box>
        </Tooltip>
        {!collapsed && !isMobile && (
          <Tooltip title="Collapse sidebar">
            <IconButton size="small" onClick={toggleSidebarCollapsed} aria-label="Collapse sidebar">
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />
      <List sx={{ flex: 1, px: 1 }}>
        {/* On mobile the bottom nav already owns Dashboard / Calendar /
            Contacts, so the drawer ("More") lists only the SECONDARY
            destinations — no overlap. Desktop shows the full list. */}
        {(isMobile
          ? menuItems.filter((i) => !['/', '/calendar', '/contacts'].includes(i.path))
          : menuItems
        ).map((item) => {
          const selected = location.pathname === item.path;
          const button = (
            <ListItemButton
              selected={selected}
              onClick={() => {
                navigate(item.path);
                if (isMobile) setMobileOpen(false);
              }}
              sx={{
                borderRadius: 2,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 2,
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  '& .MuiListItemIcon-root': { color: 'white' },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: collapsed ? 0 : 40, justifyContent: 'center' }}>
                {item.icon}
              </ListItemIcon>
              {!collapsed && <ListItemText primary={item.text} />}
            </ListItemButton>
          );
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              {collapsed ? (
                <Tooltip title={item.text} placement="right">
                  <Box sx={{ width: '100%' }}>{button}</Box>
                </Tooltip>
              ) : (
                button
              )}
            </ListItem>
          );
        })}
      </List>
      <Divider />
      {/* Account section — lives in the side menu on both mobile and desktop
          (the top-right avatar menu was removed). On desktop it hides when the
          sidebar is collapsed (the expand chevron shows there instead). */}
      {(isMobile || !collapsed) && (
        <Box sx={{ px: 1, py: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: 13 }}>
              {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {user?.name || 'Account'}
              </Typography>
              {user?.email && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.2, fontSize: '0.7rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {user.email}
                </Typography>
              )}
            </Box>
          </Box>
          {/* One "Profile & Settings" entry — the old "Settings" item went to
              the same /profile page, so it was a duplicate. */}
          <ListItemButton
            dense
            sx={{ borderRadius: 2 }}
            onClick={() => {
              navigate('/profile');
              setMobileOpen(false);
            }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}><PersonIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Profile & Settings" primaryTypographyProps={{ variant: 'body2' }} />
          </ListItemButton>
          <ListItemButton
            dense
            sx={{ borderRadius: 2, color: 'error.main', '& .MuiListItemIcon-root': { color: 'error.main' } }}
            onClick={() => {
              setMobileOpen(false);
              handleLogout();
            }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}><LogoutIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ variant: 'body2' }} />
          </ListItemButton>
        </Box>
      )}
      {collapsed && !isMobile ? (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton size="small" onClick={toggleSidebarCollapsed} aria-label="Expand sidebar">
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            LevitanMD v1.0.0
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* App Bar — hidden in immersive mode (mobile patient chart open):
          the sticky patient row owns the top of the screen there, and the
          back arrow is the exit. Reappears on the contact list. */}
      <AppBar
        position="fixed"
        sx={{
          display: immersive ? 'none' : 'flex',
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          ml: { md: `${effectiveDrawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        {/* Density: 56px at every width (MUI default is 64 at sm+) — part of
            the compression pass; keep Contacts' dvh budget in sync. */}
        <Toolbar sx={{ minHeight: 56, '@media (min-width: 600px)': { minHeight: 56 } }}>
          {/* Top-left hamburger: hidden for users who arrived via /m (from_m) so they
              can't open the desktop sidebar from the top-left. The bottom "More" button
              still opens the same drawer. Normal (non-/m) users keep the hamburger. */}
          {!fromMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
          
          {fromMobile && (
            <Button
              onClick={handleBackToTriage}
              sx={{ mr: 1, textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              color="primary"
            >
              ← Back to Triage
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />
          
          <Tooltip title="Notifications">
            <IconButton onClick={handleNotificationsOpen} sx={{ mr: 1 }}>
              <Badge color="error" badgeContent={unreadCount} invisible={unreadCount === 0}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* The account (name, email, Profile & Settings, Logout) lives in the
              side menu on both mobile and desktop, so the top bar keeps just the
              notifications bell. */}

          <Menu
            anchorEl={notificationAnchorEl}
            open={Boolean(notificationAnchorEl)}
            onClose={handleNotificationsClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{ mt: 1 }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Notifications
              </Typography>
            </MenuItem>
            <Divider />
            {notifications.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No notifications
                </Typography>
              </MenuItem>
            ) : (
              notifications.slice(0, 8).map((notification) => (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    alignItems: 'flex-start',
                    whiteSpace: 'normal',
                    maxWidth: 360,
                    fontWeight: notification.read ? 400 : 600,
                  }}
                >
                  <ListItemText
                    primary={notification.title}
                    secondary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {notification.message}
                        </Typography>
                        {notification.createdAt && (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(notification.createdAt).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </MenuItem>
              ))
            )}
            <Divider />
            <MenuItem onClick={handleMarkAllRead} disabled={unreadCount === 0}>
              Mark all as read
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: effectiveDrawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer (always full width when open) */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {renderDrawer(false)}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: effectiveDrawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
          open
        >
          {renderDrawer(sidebarCollapsed)}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          // Flex items default to min-width:auto and won't shrink below their
          // content — which lets a wide child (e.g. a long unwrapped line) push
          // the whole layout wider than the viewport. minWidth:0 + maxWidth keep
          // the content area within the screen so pages never scroll sideways.
          minWidth: 0,
          maxWidth: '100%',
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
          // No AppBar offset in immersive mode (bar is hidden there).
          mt: immersive ? 0 : 7,
          minHeight: immersive ? '100vh' : 'calc(100vh - 56px)',
          // Clear the fixed mobile bottom nav (+16px breathing room so the last
          // element never sits flush against it); desktop keeps the standard
          // bottom padding. Dropped in immersive mode (bar hidden) so Contacts'
          // dvh math is intact.
          pb: { xs: immersive ? 0 : 'calc(72px + env(safe-area-inset-bottom))', md: immersive ? 0 : 3 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Outlet />
      </Box>

      {/* Global mobile bottom navigation. Self-hides on desktop and in immersive
          mode; "More" opens the existing drawer (rest of the menu items). */}
      <MobileBottomNav onMore={() => setMobileOpen(true)} />

      <Dialog
        open={notificationDialogOpen}
        onClose={() => setNotificationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{selectedNotification?.title || 'Notification'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedNotification?.message || ''}
          </Typography>
          {selectedNotification?.category && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Category: {selectedNotification.category}
            </Typography>
          )}
          {selectedNotification?.priority && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Priority: {selectedNotification.priority}
            </Typography>
          )}
          {selectedNotification?.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Created: {new Date(selectedNotification.createdAt).toLocaleString()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MainLayout;

